import styled from 'styled-components';

import { COLORS, MOBILE, BLOG_PREVIEW, BLOG_LIST, STATUS_COLORS } from '../../theme/tokens';
import { thinScrollbar } from '../../theme/mixins';
import { Text } from '../../components/Text/Text';

/** Page column inside the layout content card (placeholder-page rhythm). */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 2px 2px 24px;
`;

/** Heading row: section title left, the "New article" CTA right. */
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

/**
 * Left half of the blog heading: the title and the blog's on/off switch.
 *
 * The switch belongs to the section, not to the CTA, so it stays anchored to
 * the title instead of drifting to the far edge on a wide viewport.
 */
export const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

/** Title cell: hero thumb + (name + slug) stack. */
export const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/** 16:9 hero thumbnail; the wl no-image gradient shows when the article has none. */
export const ThumbBox = styled.div`
  width: 64px;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background: ${BLOG_PREVIEW.coverFallback};
`;

export const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/** The pillar marker — the article page's hub-pill, scaled for table rows. */
export const StartHerePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 3px 10px;
  border-radius: 99px;
  background: ${BLOG_LIST.pillBg};
  color: ${BLOG_LIST.pillText};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const StartHereDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${BLOG_LIST.pillDot};
`;

/** Title cell: name + slug stack, with the pillar badge beside the name. */
export const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const ArticleTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const ArticleSlug = styled.span`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const CellText = styled(Text)`
  font-size: 14px;
  color: ${COLORS.textMuted};
`;

export const DateText = styled.span`
  font-size: 13px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

/**
 * Per-row action strip. Fixed slot widths (not flex) so View / Edit /
 * Publish|Unpublish / Delete line up in clean vertical columns across rows even
 * though the third label's width varies; each button centres inside its slot.
 * Sized for the longest label in both locales (Unpublish / Despublicar,
 * Delete / Eliminar).
 *
 * The slots are MEASURED, not guessed: each is the widest its label renders in
 * either locale (text + the button's own 16px padding), plus 2px. The widest of
 * each pair is Spanish in three of the four — Editar 50, Despublicar 85,
 * Eliminar 63 against Edit 39, Unpublish 74, Delete 54 — so sizing by the
 * English word alone clips the Mexican build, and `white-space: nowrap` means it
 * spills rather than wraps.
 *
 * The gap is gone and the slots are tight because the strip had grown loose
 * enough to read as separate controls rather than one row's worth of actions.
 * The result holds FOUR actions in 250px where three used to take 244: the
 * buttons keep their own padding, so the hit targets stay full size and only the
 * dead space between them closes up.
 */
export const RowActions = styled.div`
  display: grid;
  grid-template-columns: 46px 52px 87px 65px;
  justify-content: end;
  gap: 0;
`;

/** Compact ghost text button for table rows (a full `Button` is 48px tall). */
export const ActionButton = styled.button`
  border: none;
  background: transparent;
  color: ${COLORS.ctaText};
  font-size: 13px;
  font-weight: 500;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;

    &:hover {
      background: transparent;
    }
  }
`;

/**
 * The row action that cannot be taken back. Same shape as {@link ActionButton}
 * — it is one of a row of four and must not stand taller or wider than its
 * neighbours — carrying the negative status colour rather than the CTA one, so
 * a destructive action never looks like the three reversible ones beside it.
 *
 * Disabled on a published article, which the service refuses to delete. It
 * keeps the plain muted treatment there rather than a faded red: an action that
 * cannot be taken right now should not still be shouting.
 */
export const DangerActionButton = styled(ActionButton)`
  color: ${STATUS_COLORS.negative};

  /* color-mix, not an alpha suffix on the token: these tokens are var(--ows-…,
     #hex) references, so appending hex digits builds var(…)1a — invalid, and
     dropped in silence, which reads as a button with no hover at all. Same trap
     the LineChart ring documents. */
  &:hover:not(:disabled) {
    background: color-mix(in srgb, ${STATUS_COLORS.negative} 14%, transparent);
  }

  &:disabled {
    color: ${COLORS.textMuted};
  }
`;

/**
 * The confirm button inside a destructive modal. A solid `Button` would say
 * "primary action" in the CTA colour, which is the wrong thing to say about
 * permanent deletion — this reads as the deletion it performs. Matches the
 * shared Button's metrics so it sits level with Cancel beside it.
 */
export const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  /* The SOLID danger token, not the status-text one: STATUS_COLORS.negative is
     the colour a word turns to report a bad state (the Badge tone, and the row
     action above), which is too light to carry white text as a fill. This is the
     same fill the side menu and the feature card use. */
  background: ${COLORS.statusDanger};
  color: ${COLORS.text};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/** Modal body copy. */
export const ModalCopy = styled(Text)`
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The article name echoed inside the confirm modal. */
export const ModalArticleName = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

/**
 * Tighter panel padding around the table so hairlines span edge to edge.
 *
 * It also hosts the one rule the generating row needs from OUTSIDE itself: the
 * row Lumio is writing into traces a glow around its whole edge, and that ring
 * can only follow the row if the `<tr>` is its containing block. `position:
 * relative` provides that; the explicit `z-index` makes the row its own stacking
 * context, so the ring's `z-index` is resolved against the row rather than
 * escaping to the panel (where the neighbouring rows' hairlines would decide
 * what paints over the rim). Scoped by `:has()` to rows that carry a ring.
 */
export const TablePanelBody = styled.div`
  margin: -8px -6px -12px;

  tbody tr:has([data-generating]) {
    position: relative;
    z-index: 0;
  }

  ${MOBILE} {
    margin: -6px 0 -8px;
  }
`;

/* ── Category management ── */

/** Category-row actions: natural widths (count label + Edit + Delete). */
export const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 2px;
  border-bottom: 1px solid ${COLORS.hairline};

  &:first-child {
    padding-top: 4px;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 4px;
  }

  ${MOBILE} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const CategoryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const CategoryName = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const CategoryDescription = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/** Stacked fields inside the category modal. */
export const ModalFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/** "N articles" — the popup trigger; reads as a link, not a plain label. */
export const CountButton = styled.button`
  border: none;
  background: transparent;
  color: ${COLORS.textMuted};
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;

  &:hover {
    color: ${COLORS.text};
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    text-decoration: none;
    cursor: default;

    &:hover {
      color: ${COLORS.textMuted};
      background: transparent;
    }
  }
`;

/** Scrollable article list inside the category popup. */
export const ArticleListWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-height: min(320px, 50vh);
  overflow-y: auto;
  margin: 0 -8px;
  ${thinScrollbar}
`;

/** One article row in the popup: title + slug left, status badge right. */
export const ArticleRowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 8px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: ${COLORS.surfaceFaint};
  }
`;

/** The pager under the article table, set off by a hairline as the players
 *  roster's is. The top margin gives back what `TablePanelBody` pulls in, and
 *  the sides bleed as the table does, which is what keeps a three-page pager
 *  on one line on a 360px phone. */
export const PagerFooter = styled.div`
  margin: 12px -6px 0;
  padding: 14px 0 2px;
  border-top: 1px solid ${COLORS.hairline};
`;

/** Centred retry action under the load-failure message. */
export const RetryWrap = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 8px;
`;

/**
 * Carrier for the reason a header action is refused.
 *
 * A disabled `<button>` receives no pointer events, so a `title` on the button
 * itself never surfaces as a tooltip. The row actions get away with it because
 * they sit in a table cell the pointer can still reach; a lone header button
 * does not, so the attribute goes on a wrapper that stays hoverable.
 */
export const ActionReason = styled.span`
  display: inline-flex;
`;

/** Placeholder rows while the category list is still being read. */
export const CategorySkeletons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px 2px;
`;
